import { IconCurrencyDollar, IconQuestionMark } from "@tabler/icons-react"
import { type FC, type ReactNode } from "react"

import { EventAttendee, EventPurchase } from "@forento/core/models/event"
import { toNumberFormat } from "@forento/core/utilities/number"
import CommunityProfileAvatar from "@forento/ui/components/CommunityProfileAvatar"

import Button from "~/components/Button"
import { CheckmarkIcon, CloseIcon } from "~/components/Icon"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalPadding, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

type Props = { eventId: number; isOpen: boolean; onClose(): void }
const UsersModal: FC<Props> = ({ eventId, isOpen, onClose }) => {
	const attendees = query.event.listAttendees.useQuery(eventId, { enabled: isOpen })
	const purchases = query.event.listPurchases.useQuery(eventId, { enabled: isOpen })

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen}>
			<ModalTitle>Users</ModalTitle>
			{attendees.error || purchases.error ? (
				<ModalText>Failed to load users. Please try again later.</ModalText>
			) : !attendees.data || !purchases.data ? (
				<PartialLoadingPage />
			) : (
				<Content attendees={attendees.data} purchases={purchases.data} />
			)}
			<ModalButtons>
				<Button variant="primary" onClick={onClose}>
					Close
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

type ContentProps = { attendees: EventAttendee[]; purchases: EventPurchase[] }
const Content: FC<ContentProps> = ({ attendees, purchases }) => {
	const attending = attendees.filter(x => x.response === "attending")
	const interested = attendees.filter(x => x.response === "interested")
	const notInterested = attendees.filter(x => x.response === "not-interested")
	const otherPurchases = purchases.filter(x => !attendees.some(y => y.user.id === x.user.id))

	return (
		<ModalPadding className="flex flex-col gap-4">
			<Title label="Attending" count={attending.length} />
			{attending.map(attendee => (
				<User
					key={`attending-${attendee.user.id}`}
					user={attendee.user}
					icon={<CheckmarkIcon className="text-success size-6" />}
				/>
			))}
			<Title label="Interested" count={interested.length} />
			{interested.map(attendee => (
				<User
					key={`interested-${attendee.user.id}`}
					user={attendee.user}
					icon={<IconQuestionMark className="size-6 text-yellow-500" />}
				/>
			))}
			<Title label="Not interested" count={notInterested.length} />
			{notInterested.map(attendee => (
				<User
					key={`not-interested-${attendee.user.id}`}
					user={attendee.user}
					icon={<CloseIcon className="text-danger size-6" />}
				/>
			))}
			<Title label="Purchases" count={otherPurchases.length} />
			{otherPurchases.map(purchase => (
				<User
					key={`${purchase.type}-${purchase.id}`}
					user={purchase.user}
					icon={<IconCurrencyDollar className="size-6 text-yellow-500" />}
				/>
			))}
		</ModalPadding>
	)
}

const Title: FC<{ label: string; count: number }> = ({ label, count }) => (
	<h2 className="flex items-center gap-2 font-semibold">
		{label} <span className="size-1 rounded-full bg-slate-400" /> {toNumberFormat(count)}
	</h2>
)

type UserProps = { user: { id: number; firstName: string; lastName: string; avatarFilePath: string }; icon: ReactNode }
const User: FC<UserProps> = ({ user, icon }) => (
	<div className="flex items-center gap-3">
		{icon}
		<CommunityProfileAvatar size={32} profile={user} />
		<Link className="text-sm font-semibold" to={routes.user.detail(user.id)}>
			{user.firstName} {user.lastName}
		</Link>
	</div>
)

export default UsersModal
